import React, { useState } from 'react';
import axios from 'axios';
import { ReviewerRef } from './types';

/**
 * DM routing (2026-09-02, Fredrik) — shows and edits a cycle's assigned
 * decision maker(s). Assignment routes DM-facing notifications only
 * (assigned → default DMs → all DMs); it never gates what any DM or
 * admin can see or do. Same contract as DeadlineEditor: parent passes
 * getAuthHeaders + onUpdated and decides edit rights via canEdit.
 */
interface AssignedDmEditorProps {
  cycleId: string;
  current: ReviewerRef[];
  getAuthHeaders: () => Promise<Record<string, string>>;
  onUpdated: () => void;
  canEdit: boolean;
}

interface TeamMember {
  _id: string;
  name: string;
  email: string;
  role?: string;
  permissions?: { subcategories?: string[] };
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const isDmCapable = (m: TeamMember) =>
  m.role === 'admin' || m.permissions?.subcategories?.includes('content_review.decision_maker');

const AssignedDmEditor: React.FC<AssignedDmEditorProps> = ({
  cycleId, current, getAuthHeaders, onUpdated, canEdit
}) => {
  const [editing, setEditing] = useState(false);
  const [candidates, setCandidates] = useState<TeamMember[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const openEditor = async () => {
    setMsg(null);
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get(`${backendUrl}/api/team`, { headers });
      setCandidates((res.data || []).filter(isDmCapable));
      setSelectedIds((current || []).map(d => d._id));
      setEditing(true);
    } catch {
      setMsg('Failed to load team');
    }
  };

  const toggle = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setMsg(null);
    try {
      setSaving(true);
      const headers = await getAuthHeaders();
      await axios.put(
        `${backendUrl}/api/content-review/cycles/${cycleId}/assigned-decision-makers`,
        { memberIds: selectedIds },
        { headers }
      );
      setEditing(false);
      onUpdated();
    } catch (err: any) {
      setMsg(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const names = (current || []).map(d => d.name).join(', ');

  return (
    <div className="assigned-dm-editor">
      <div className="deadline-editor-row">
        <span className="deadline-editor-label">Decision maker{(current || []).length > 1 ? 's' : ''}:</span>
        <span className="assigned-dm-names">
          {names || 'Not assigned — notifications go to the default DMs (or all DMs)'}
        </span>
        {canEdit && !editing && (
          <button type="button" className="deadline-action-btn" onClick={openEditor}>
            Edit
          </button>
        )}
        {msg && <span className="deadline-msg deadline-msg-error">{msg}</span>}
      </div>
      {editing && (
        <div className="assigned-dm-picker">
          {candidates.map(m => (
            <label key={m._id} className="assigned-dm-option">
              <input
                type="checkbox"
                checked={selectedIds.includes(m._id)}
                onChange={() => toggle(m._id)}
              />
              {m.name} <span className="assigned-dm-email">({m.email})</span>
            </label>
          ))}
          <div className="deadline-editor-row">
            <button
              type="button"
              className="deadline-action-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="deadline-action-btn deadline-clear-btn"
              onClick={() => { setEditing(false); setMsg(null); }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
          <p className="deadline-helper-text">
            Notifications for this cycle go to the selected decision makers. Leave all
            unchecked to fall back to the default DMs (Team Settings), or to every DM
            if no default is set. Assignment never limits who can act on the cycle.
          </p>
        </div>
      )}
    </div>
  );
};

export default AssignedDmEditor;
